export function formatPrice(price: number | undefined | null, maximumFractionDigits: number = 2) {
  if (price === null || price === undefined) return "—";
  return Number(price).toLocaleString("en-IN", {
    minimumFractionDigits: maximumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
  });
}

export function formatPnL(pnl: number) {
  const formatted = formatPrice(pnl);
  if (pnl > 0) return `+${formatted}`;
  if (pnl < 0) return `-${formatted}`;
  return formatted;
}

export function formatDate(dateString: string | Date | number | undefined | null) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return String(dateString);
  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatTime(dateString: string | Date | number | undefined | null, includeSeconds: boolean = true) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return String(dateString);
  return date.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: includeSeconds ? "2-digit" : undefined,
    hour12: false,
  });
}

export function formatISTTime(dateString: string | Date | number | undefined | null, includeSeconds: boolean = true) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return String(dateString);
  return (
    date.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: includeSeconds ? "2-digit" : undefined,
      hour12: false,
    }) + " IST"
  );
}

export function formatISTDateTime(dateString: string | Date | number | undefined | null) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return String(dateString);
  return (
    date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }) + " IST"
  );
}

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const calculatePnlPercentage = (x, y) => {
  if (y === 0) {
    return "Cannot divide by zero"; // Handle division by zero
  }
  return Number(((x - y) / y) * 100).toFixed(1);
};
export const calculatePercentage = (x, y) => {
  if (y === 0) {
    return "Cannot divide by zero"; // Handle division by zero
  }
  return Number((x / y) * 100).toFixed(2);
};

export const TICK_SIZE = 0.1;
export const MAX_SL_BUFFER = 1.5;

export function roundToTick(
  price: number,
  tickSize: number = TICK_SIZE
): number {
  return Math.round(price / tickSize) * tickSize;
}

export function isValidStopLoss(trigger: number, limit: number): boolean {
  return Math.abs(limit - trigger) <= MAX_SL_BUFFER && limit < trigger;
}

export const calculateFundamentalScore = (fundamentals: {
  summaryDetail: {
    trailingPE: number;
    dividendYield: number;
  };
  defaultKeyStatistics: {
    earningsQuarterlyGrowth: number;
  };
  financialData: {
    debtToEquity: number;
    revenueGrowth: number;
  };
}): number => {
  let score = 0;

  // Trailing P/E (25%)
  if (fundamentals?.summaryDetail?.trailingPE !== undefined) {
    score +=
      fundamentals?.summaryDetail?.trailingPE < 15
        ? 25
        : fundamentals?.summaryDetail?.trailingPE <= 20
          ? 15
          : fundamentals?.summaryDetail?.trailingPE <= 25
            ? 10
            : 0;
  }

  // Earnings Growth (25%)
  if (
    fundamentals?.defaultKeyStatistics?.earningsQuarterlyGrowth !== undefined
  ) {
    score +=
      fundamentals?.defaultKeyStatistics?.earningsQuarterlyGrowth > 0.1
        ? 25
        : fundamentals?.defaultKeyStatistics?.earningsQuarterlyGrowth >= 0.05
          ? 15
          : fundamentals?.defaultKeyStatistics?.earningsQuarterlyGrowth >= 0
            ? 5
            : 0;
  }

  // Debt-to-Equity (20%)
  if (fundamentals?.financialData?.debtToEquity !== undefined) {
    score +=
      fundamentals?.financialData?.debtToEquity < 1
        ? 20
        : fundamentals?.financialData?.debtToEquity <= 2
          ? 10
          : 0;
  }

  // Revenue Growth (20%)
  if (fundamentals?.financialData?.revenueGrowth !== undefined) {
    score +=
      fundamentals?.financialData?.revenueGrowth > 0.05
        ? 20
        : fundamentals?.financialData?.revenueGrowth >= 0.02
          ? 10
          : 0;
  }

  // Dividend Yield (10%)
  if (fundamentals?.summaryDetail?.dividendYield !== undefined) {
    score +=
      fundamentals?.summaryDetail?.dividendYield > 0.02
        ? 10
        : fundamentals?.summaryDetail?.dividendYield >= 0.01
          ? 5
          : 0;
  }

  return Math.min(100, score); // Cap at 100
};

export function calculateEMA(closes: number[], period: number): number {
  if (closes.length < period) {
    throw new Error(
      `Insufficient data: need at least ${period} closing prices`
    );
  }

  // Use the last 'period' closes for EMA calculation
  const data = closes.slice(-period);

  // Simple Moving Average (SMA) for the first EMA point
  let sma = data.reduce((sum, price) => sum + price, 0) / period;

  // Smoothing factor (multiplier)
  const multiplier = 2 / (period + 1);

  // Calculate EMA iteratively
  let ema = sma;
  for (let i = 1; i < data.length; i++) {
    ema = (data[i] - ema) * multiplier + ema;
  }

  return Number(ema.toFixed(2)); // Return with 2 decimal places for consistency
}
